// LeadRow component - single row in the lead table
// Shows lead info and "View Details" button


/**
 * 
 * NOTES-SUMMARY-LEAD ROW
 * 
 * What this component does:
 *  - Displays one row in the table
 *  - Colors the score (green/yellow/red)
 *  - "View Details" button opens modal with full lead info
 * 
 */



import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStickyNote, faReply, faMousePointer, faEye, faMinus, faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import { getScoreColor, getActivityLevel, ACTIVITY_LEVELS } from '../../utils/constants';
import '../../styles/components.css';

function LeadRow({ lead, onLeadClick, isSelected, onSelect }) {
  // Get color based on score
  const scoreColor = getScoreColor(lead.score || 0);
  
  // Get activity level for this lead
  const activityLevel = getActivityLevel(lead);
  
  // Get interest status badge
  const getInterestBadge = (interestStatus) => {
    const statusConfig = {
      interested: { bg: '#dcfce7', color: '#22c55e', label: 'Interested' },
      meeting_booked: { bg: '#dbeafe', color: '#3b82f6', label: 'Meeting Booked' },
      meeting_completed: { bg: '#ede9fe', color: '#8b5cf6', label: 'Meeting Done' },
      closed: { bg: '#cffafe', color: '#06b6d4', label: 'Closed' },
      neutral: { bg: '#f3f4f6', color: '#6b7280', label: 'Neutral' },
      not_interested: { bg: '#fee2e2', color: '#ef4444', label: 'Not Interested' },
      do_not_contact: { bg: '#fecaca', color: '#991b1b', label: 'Do Not Contact' },
      wrong_person: { bg: '#fef3c7', color: '#f59e0b', label: 'Wrong Person' },
      out_of_office: { bg: '#e2e8f0', color: '#64748b', label: 'Out of Office' },
      unknown: { bg: '#f9fafb', color: '#9ca3af', label: '—' }
    };
    
    const config = statusConfig[interestStatus] || statusConfig.unknown;
    
    return (
      <span 
        style={{
          backgroundColor: config.bg,
          color: config.color,
          padding: '4px 10px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '500'
        }}
      >
        {config.label}
      </span>
    );
  };
  
  // Get activity badge details
  const getActivityBadge = () => {
    switch (activityLevel) {
      case ACTIVITY_LEVELS.REPLIED:
        return {
          icon: faReply,
          text: 'Replied',
          className: 'activity-badge-replied',
          color: '#10b981' // green
        };
      case ACTIVITY_LEVELS.SUBMITTED:
        return {
          icon: faPaperPlane,
          text: 'Submitted',
          className: 'activity-badge-submitted',
          color: '#10b981' // green
        };
      case ACTIVITY_LEVELS.CLICKED:
        return {
          icon: faMousePointer,
          text: 'Clicked',
          className: 'activity-badge-clicked',
          color: '#3b82f6' // blue
        };
      case ACTIVITY_LEVELS.OPENED:
        return {
          icon: faEye,
          text: 'Opened',
          className: 'activity-badge-opened',
          color: '#f59e0b' // yellow/orange
        };
      case ACTIVITY_LEVELS.INACTIVE:
      default:
        return {
          icon: faMinus,
          text: 'Inactive',
          className: 'activity-badge-inactive',
          color: '#6b7280' // gray
        };
    }
  };
  
  const activityBadge = getActivityBadge();
  
  return (
    <tr className="lead-row">
      <td style={{ textAlign: 'center', width: '50px' }}>
        <input
          type="checkbox"
          checked={isSelected || false}
          onChange={() => {
            console.log('Checkbox onChange for lead:', lead._id, 'isSelected was:', isSelected);
            onSelect(lead._id);
          }}
          onClick={(e) => {
            console.log('Checkbox onClick for lead:', lead._id);
            e.stopPropagation();
          }}
          style={{ cursor: 'pointer' }}
        />
      </td>
      <td>
        <span 
          className="lead-score"
          style={{ 
            backgroundColor: scoreColor,
            color: 'white',
            padding: '4px 8px',
            borderRadius: '4px',
            fontWeight: 'bold'
          }}
        >
          {lead.score ?? 0}
        </span>
      </td>
      <td>{lead.name || 'N/A'}</td>
      <td>{lead.email}</td>
      <td>{lead.company || 'N/A'}</td>
      <td>{lead.enrichment?.job_title || 'N/A'}</td>
      <td>
        <span className={`stage-badge stage-${lead.stage}`}>
          {lead.stage || 'cold'}
        </span>
      </td>
      <td>
        {getInterestBadge(lead.interest_status)}
      </td>
      <td>
        <div className="activity-badge-container">
          <span className={`activity-badge ${activityBadge.className}`}>
            <FontAwesomeIcon 
              icon={activityBadge.icon} 
              style={{ 
                color: activityBadge.color,
                marginRight: '6px'
              }} 
            />
            {activityBadge.text}
          </span>
        </div>
      </td>
      <td>
        <div className="notes-cell">
          <FontAwesomeIcon 
            icon={faStickyNote} 
            style={{ 
              color: lead.notes && lead.notes.length > 0 ? '#059669' : '#d1d5db',
              marginRight: '4px'
            }} 
          />
          {lead.notes && lead.notes.length > 0 ? lead.notes.length : ''}
        </div>
      </td>
      <td>
        <span style={{ color: lead.assigned_to ? '#374151' : '#9ca3af' }}>
          {lead.assigned_to || 'Unassigned'}
        </span>
      </td>
      <td>
        <button 
          onClick={() => onLeadClick(lead)}
          className="btn btn-sm btn-primary"
        >
          View Details
        </button>
      </td>
    </tr>
  );
}

export default LeadRow;